import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { callApi } from "../../api/apiCaller";
import { BASE_URL, routes } from "../../api/routes";
import { GreenNotify, RedNotify } from "../../helper/helper";
import { Col, Input, Row } from "antd";
import Loader from "../loader/loader";

const EditEmployer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const dummyImage =
    "https://novathreadbucket.s3.amazonaws.com/nova-app-1685176470232-dummy.PNG";
  
  const [name, setName] = useState(location?.state?.item?.name || "");
  const [employerCode, setEmployerCode] = useState(
    location?.state?.item?.employerCode ||
      location?.state?.item?.code ||
      location?.state?.item?.shortCode ||
      ""
  );
  const [image, setImage] = useState(location?.state?.item?.image || "");

  const [isloading, setIsLoading] = useState(false);
  const id = location?.state?.item?._id;
  const fileInputImageRef = useRef(null);

  const pickImageFile = () => {
    fileInputImageRef.current.click();
  };

  const handleImageUpload = (evt) => {
    const files = evt.target.files;
    const file = files[0];
    
    if (!file) {
      // Reset input to allow same file selection
      if (fileInputImageRef.current) {
        fileInputImageRef.current.value = '';
      }
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      RedNotify("Invalid file type. Please select JPG, PNG, or GIF image.");
      if (fileInputImageRef.current) {
        fileInputImageRef.current.value = '';
      }
      return;
    }

    // Validate file size (Max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      RedNotify("File size is too large. Maximum file size is 5MB.");
      if (fileInputImageRef.current) {
        fileInputImageRef.current.value = '';
      }
      return;
    }

    // Create custom upload handler with proper error handling
    setIsLoading(true);
    
    var formdata = new FormData();
    formdata.append("file", file);

    var requestOptions = {
      method: "POST",
      body: formdata,
      redirect: "follow",
    };

    fetch(`${BASE_URL}api/v1/user/upload`, requestOptions)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.url) {
          setImage(data.url);
          GreenNotify("Image uploaded successfully!");
          setIsLoading(false);
        } else {
          throw new Error("Upload failed: No URL received from server");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Upload error:", error);
        RedNotify(error.message || "Failed to upload image. Please try again.");
      })
      .finally(() => {
        // Reset input value to allow selecting the same file again
        setTimeout(() => {
          if (fileInputImageRef.current) {
            fileInputImageRef.current.value = '';
          }
        }, 100);
      });
  };

  const UpdateEmployer = () => {
    let getRes = (res) => {
      GreenNotify("Employer is updated successfully");
      navigate("/employer");
    };

    let body = {
      name: name.trim(),
      employerCode: employerCode.trim().toUpperCase(),
      image: image,
    };

    callApi(
      "PATCH",
      `${routes.updateEmployer}/${id}`,
      body,
      setIsLoading,
      getRes,
      () => {}
    );
  };

  return (
    <div className="modern-page-container">
      <Loader loading={isloading} />
      
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h1 className="page-title">Edit Employer</h1>
            <p className="page-subtitle">Update employer information</p>
          </div>
          <button
            onClick={() => navigate("/employer")}
            className="modern-back-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Employers
          </button>
        </div>
      </div>

      {/* Form Card */}
      <div className="form-card">
        <div className="form-header">
          <h2 className="form-title">Employer Details</h2>
          <p className="form-description">Update the fields below to edit employer information</p>
        </div>

        <div className="form-content">
          <Row gutter={[32, 24]}>
            <Col xs={24} lg={12}>
              <div className="form-group">
                <label className="form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                    <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Employer Name
                  <span className="required-star">*</span>
                </label>
                <Input
                  placeholder="Enter employer name"
                  className="modern-input"
                  size="large"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </Col>

            <Col xs={24} lg={12}>
              <div className="form-group">
                <label className="form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                    <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Employer Code
                  <span className="required-star">*</span>
                </label>
                <Input
                  placeholder="Enter employer code"
                  className="modern-input"
                  size="large"
                  value={employerCode}
                  onChange={(e) =>
                    setEmployerCode(
                      e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "")
                    )
                  }
                  maxLength={20}
                  required
                />
                <p className="image-help-text">
                  Use the employer code at the start of the file name, for
                  example <strong>AHLI</strong> from
                  <strong> AHLI_AR_20260527_1052.xlsx</strong>.
                </p>
              </div>
            </Col>

            <Col xs={24} lg={12}>
              <div className="form-group">
                <label className="form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                    <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Employer Image
                  <span className="required-star">*</span>
                </label>
                <div className="modern-image-upload-container">
                  <div className="image-preview-wrapper" onClick={pickImageFile}>
                    <img
                      alt="Employer"
                      src={image?.length ? image : dummyImage}
                      className="image-preview"
                    />
                    <div className="image-overlay">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17 8L12 3L7 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 3V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <p>Click to upload</p>
                    </div>
                  </div>
                  <input
                    type="file"
                    ref={fileInputImageRef}
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                    accept="image/*"
                  />
                  <p className="image-help-text">Supported formats: JPG, PNG, GIF (Max 5MB)</p>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div className="form-footer">
          <button
            onClick={() => navigate("/employer")}
            className="cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={UpdateEmployer}
            className="submit-btn"
            disabled={!name.trim() || !employerCode.trim() || !image}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Update Employer
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEmployer;
